/**
 * usePortfolioData Hook
 *
 * Unified data fetching hook for portfolio table views.
 * Handles all three contexts (user dashboard, admin view, rebalance) with consistent data.
 *
 * Key behaviors:
 * - Uses admin endpoints when userId is provided (auth-driven, not route-driven)
 * - ALWAYS fetches batch prices for consistent pricing across all views
 * - Calculates diffs and synthetics when targetPortfolio is provided
 * - Returns unified PortfolioRow[] format for all contexts
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { PortfolioRow } from "../components/portfolio/columns/columnTypes";
import { PortfolioTableConfig } from "../components/portfolio/configs/types";
import { contractsService } from "../services/contractsService";
import marketDataService from "../services/marketDataService";
import { optionChainsV2Service } from "../services/optionChainsV2Service";
import { RebalanceEventContract } from "../types/rebalanceContract";
import tradeCalculationService, {
    FxRates,
    PositionWithDiff,
    SyntheticPosition,
} from "../services/tradeCalculationService";
import { IbkrAccountSummary } from "../types";
import { OptionPriceResult, StockPriceResult } from "../types/marketData";
import { accountQueryKeys, useAccountSummary, usePositions } from "./useAccountData";
import { fxRatesQueryKeys, useFxRates } from "./useFxRates";
import {
  getFxRate,
  isOptionPosition,
  positionToRow,
} from "./portfolioDataCore";

// ============================================================================
// Types
// ============================================================================

export interface UsePortfolioDataParams {
  /** User ID for admin view. undefined = current user, string = admin viewing user */
  userId?: string;
  /** Target portfolio contracts for rebalance diff mode */
  targetPortfolio?: RebalanceEventContract[];
  /** Symbol lookup map (conId -> symbol) for rebalance */
  tickerDataMap?: Record<number, string>;
  /** Contract IDs excluded from diff calculations */
  excludedConIds?: Set<number>;
  /** Portfolio table configuration */
  config: PortfolioTableConfig;
  /** If true, positions not in target are marked for SELL; if false, only target contracts get trades */
  fullRefresh?: boolean;
  /** When false, disables positions and summary fetching (default: true) */
  enabled?: boolean;
}

export interface UsePortfolioDataResult {
  /** Unified portfolio rows (positions + synthetics if applicable) */
  rows: PortfolioRow[];
  /** Account summary data */
  accountSummary: IbkrAccountSummary | null;
  /** Whether any data is still loading */
  isLoading: boolean;
  /** Whether rows have partial data (e.g., FX rates still loading, USD values are null) */
  isPartialData: boolean;
  /** First error encountered, if any */
  error: Error | null;
  /** Timestamp of most recent data update */
  lastUpdatedAt: number;
  /** Function to refresh all data. Resolves when all queries have refetched. */
  refresh: () => Promise<void>;
}

// ============================================================================
// Query Keys
// ============================================================================

export const portfolioQueryKeys = {
  batchPrices: (conIds: number[]) => ["portfolio", "batchPrices", conIds.sort().join(",")] as const,
  tradableTimes: (conIds: number[]) => ["portfolio", "tradableTimes", conIds.sort().join(",")] as const,
  contractDetails: (conIds: number[]) => ["portfolio", "contractDetails", conIds.sort().join(",")] as const,
  optionPrices: (conIds: number[]) => ["portfolio", "optionPrices", conIds.sort().join(",")] as const,
  optionMultipliers: (conIds: number[]) => ["portfolio", "optionMultipliers", conIds.sort().join(",")] as const,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determine trade action from trade quantity
 */
function getTradeAction(tradeQty: number | null): "BUY" | "SELL" | "HOLD" | null {
  if (tradeQty === null) return null;
  if (tradeQty > 0) return "BUY";
  if (tradeQty < 0) return "SELL";
  return "HOLD";
}

/**
 * Convert PositionWithDiff or SyntheticPosition to PortfolioRow
 */
function diffPositionToRow(
  pos: PositionWithDiff | SyntheticPosition,
  priceMap: Record<number, { price: number; currency: string }>,
  timeZoneMap?: Record<number, string>,
  fxRates?: FxRates
): PortfolioRow {
  const isSynthetic = "isSynthetic" in pos && pos.isSynthetic === true;
  const priceInfo = priceMap[pos.conId];
  const price = priceInfo?.price ?? (Number(pos.mktPrice) || 0);
  const currency = priceInfo?.currency ?? (pos as PositionWithDiff).currency ?? "USD";
  const fxRate = getFxRate(currency, fxRates ?? {});
  const mktValue = Number(pos.mktValue) || 0;
  const unrealizedPnl = Number(pos.unrealizedPnl) || 0;

  // Calculate USD values (null if FX rate unavailable)
  const priceUsd = fxRate !== null ? price * fxRate : null;
  const mktValueUsd = fxRate !== null ? mktValue * fxRate : null;
  const unrealizedPnlUsd = fxRate !== null ? unrealizedPnl * fxRate : null;

  return {
    conId: pos.conId,
    symbol: pos.symbol || (pos as PositionWithDiff).description || `ConId: ${pos.conId}`,
    currency,
    shares: isSynthetic ? 0 : Number((pos as PositionWithDiff).position) || 0,
    avgCost: Number(pos.avgCost) || 0,
    mktPrice: price,
    mktValue,
    realizedPnl: Number(pos.realizedPnl) || 0,
    unrealizedPnl,
    group: isSynthetic ? null : (pos as PositionWithDiff).group ?? null,
    sector: isSynthetic ? null : (pos as PositionWithDiff).sector ?? null,
    secType: isSynthetic ? (pos as SyntheticPosition).secType : (pos as PositionWithDiff).secType ?? null,
    // USD-converted values (null if FX rate unavailable)
    priceUsd,
    mktValueUsd,
    unrealizedPnlUsd,
    // Diff fields
    currentWeightPct: pos.currentWeightPct,
    targetWeightPct: pos.targetWeightPct,
    targetQty: pos.targetQty,
    rawTargetQty: pos.rawTargetQty ?? null,
    effectiveTargetQty: pos.effectiveTargetQty ?? null,
    tradeQty: pos.tradeQty,
    tradeValue: pos.tradeValue,
    tradeValueUsd: pos.tradeValueUsd,
    fxRate,
    tradeAction: getTradeAction(pos.tradeQty),
    // Metadata
    isSynthetic,
    isInTarget: pos.isInTarget,
    timeZoneId: timeZoneMap?.[pos.conId] ?? null,
    minSize: pos.minSize ?? null,
    // Rebalance Event fields (null - populated by rebalance event view)
    userDiffId: null,
    isHeld: false,
    isExcluded: false,
    exclusionReasons: [],
    stageId: null,
    stageName: null,
    stageState: null,
    stageStartTime: null,
    stageEndTime: null,
    stageQty: null,
    hasDrift: false,
    orderId: null,
    orderStatus: null,
    filledQty: null,
    avgFillPrice: null,
    commission: null,
  };
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function usePortfolioData(params: UsePortfolioDataParams): UsePortfolioDataResult {
  const { userId, targetPortfolio, tickerDataMap = {}, excludedConIds = new Set(), config, fullRefresh = true, enabled = true } = params;
  const queryClient = useQueryClient();

  // Determine if we're in diff mode
  const isDiffMode = targetPortfolio && targetPortfolio.length > 0;

  // 1. Fetch positions (user or admin endpoint based on userId)
  const {
    data: positions = [],
    isLoading: positionsLoading,
    error: positionsError,
    dataUpdatedAt: positionsUpdatedAt,
  } = usePositions(userId, enabled);

  // Filter out zero-quantity positions in non-diff mode (closed positions from IBKR).
  // Diff mode retains all positions for accurate rebalance calculations.
  const activePositions = useMemo(() => {
    if (isDiffMode) return positions;
    return positions.filter(p => Number(p.position) !== 0);
  }, [positions, isDiffMode]);

  // 2. Fetch account summary (user or admin endpoint based on userId)
  const {
    data: accountSummary,
    isLoading: summaryLoading,
    error: summaryError,
    dataUpdatedAt: summaryUpdatedAt,
  } = useAccountSummary(userId, enabled);

  // 3. Collect all conIds to price (positions + target portfolio)
  const conIds = useMemo(() => {
    const positionConIds = activePositions.map(p => p.conId);
    const targetConIds = targetPortfolio?.map(c => c.conid).filter((id): id is number => id !== undefined) ?? [];
    // Deduplicate using Array.from for compatibility
    const combined = positionConIds.concat(targetConIds);
    return Array.from(new Set(combined));
  }, [activePositions, targetPortfolio]);

  // 3b. Option conIds among active positions -- priced via the options API + multiplier metadata.
  const optionConIds = useMemo(() => {
    return Array.from(new Set(
      activePositions.filter(p => isOptionPosition(p.secType)).map(p => p.conId)
    ));
  }, [activePositions]);

  // 4. Progressive batch prices state - updates as chunks complete
  // New API returns { price, timestamp } per conId (no currency -- derive from contract metadata per D1)
  const [partialRawPrices, setPartialRawPrices] = useState<Record<number, StockPriceResult>>({});
  const [pricesProgress, setPricesProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  
  // Track if we're currently fetching to avoid stale progress callbacks
  const fetchIdRef = useRef(0);

  // Memoized progress callback to avoid unnecessary re-renders
  const handlePriceProgress = useCallback((
    partial: Record<number, StockPriceResult>,
    completedChunks: number,
    totalChunks: number,
    currentFetchId: number
  ) => {
    // Only update if this is still the current fetch
    if (currentFetchId === fetchIdRef.current) {
      setPartialRawPrices(partial);
      setPricesProgress({ completed: completedChunks, total: totalChunks });
    }
  }, []);

  // 4b. Fetch batch prices with chunking for large position lists
  // Uses new /v2/market-data/stocks endpoint (PWA-1180)
  const {
    data: rawBatchPrices = {},
    isLoading: pricesLoading,
    error: pricesError,
    dataUpdatedAt: pricesUpdatedAt,
  } = useQuery({
    queryKey: portfolioQueryKeys.batchPrices(conIds),
    queryFn: async () => {
      // Increment fetch ID to invalidate any in-flight progress callbacks
      const currentFetchId = ++fetchIdRef.current;
      
      // Reset progress state for new fetch
      setPartialRawPrices({});
      setPricesProgress({ completed: 0, total: 0 });

      // Use chunked method for progressive loading
      const result = await marketDataService.getStockPricesChunked(conIds, {
        chunkSize: 50,
        maxConcurrent: 3,
        retryAttempts: 2,
        onProgress: (partial, completed, total) => {
          handlePriceProgress(partial, completed, total, currentFetchId);
        },
      });

      return result;
    },
    enabled: conIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds
    retry: false, // Chunking handles retries internally per-chunk
  });

  // Use partial prices for immediate display while full batch is loading
  const effectiveRawPrices = pricesLoading && Object.keys(partialRawPrices).length > 0
    ? partialRawPrices
    : rawBatchPrices;

  // 5. Fetch tradable times (only if config requires it)
  const {
    data: timeZoneMap = {},
    isLoading: tradableTimesLoading,
    error: tradableTimesError,
    dataUpdatedAt: tradableTimesUpdatedAt,
  } = useQuery({
    queryKey: portfolioQueryKeys.tradableTimes(conIds),
    queryFn: () => contractsService.getTimeZoneMap(conIds),
    enabled: config.dataRequirements.tradableTimes && conIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - trading hours don't change often
  });

  // 5b. Fetch contract details for minSize (lot size)
  const {
    data: contractDetailsMap = {},
    isLoading: contractDetailsLoading,
    error: contractDetailsError,
  } = useQuery({
    queryKey: portfolioQueryKeys.contractDetails(conIds),
    queryFn: () => contractsService.getContractDetailsMap(conIds),
    enabled: conIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - contract details don't change often
  });

  // 5d. Fetch live option quotes (bid/ask/close) for option positions.
  // Self-degrading: on failure resolves to {} so option rows fall back to the IBKR snapshot.
  const {
    data: optionPricesMap = {},
    isLoading: optionPricesLoading,
    dataUpdatedAt: optionPricesUpdatedAt,
  } = useQuery({
    queryKey: portfolioQueryKeys.optionPrices(optionConIds),
    queryFn: async (): Promise<Record<number, OptionPriceResult>> => {
      try {
        return await marketDataService.getOptionPrices(optionConIds);
      } catch {
        return {};
      }
    },
    enabled: optionConIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds - matches stock prices
    retry: false,
  });

  // 5e. Fetch option contract multipliers from the option-chains store. Self-degrading.
  const {
    data: optionMultiplierMap = {},
    isLoading: optionMultipliersLoading,
  } = useQuery({
    queryKey: portfolioQueryKeys.optionMultipliers(optionConIds),
    queryFn: async (): Promise<Record<number, number>> => {
      try {
        const res = await optionChainsV2Service.findOptionContractsByConIds(optionConIds);
        const map: Record<number, number> = {};
        for (const contract of res.optionContracts) {
          map[contract.conId] = Number(contract.multiplier) || 0;
        }
        return map;
      } catch {
        return {};
      }
    },
    enabled: optionConIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - contract metadata is stable
    retry: false,
  });

  // 5c. Merge raw prices with currency from contract metadata (D1: currency not in new API)
  // Preserves the { price, currency } shape used by positionToRow / diffPositionToRow
  const effectiveBatchPrices = useMemo((): Record<number, { price: number; currency: string }> => {
    const merged: Record<number, { price: number; currency: string }> = {};
    for (const [conIdStr, priceData] of Object.entries(effectiveRawPrices)) {
      const conId = Number(conIdStr);
      // Currency priority: contractDetailsMap > position data > default USD
      const currency =
        contractDetailsMap[conId]?.currency ??
        activePositions.find(p => p.conId === conId)?.currency ??
        "USD";
      merged[conId] = { price: priceData.price, currency };
    }
    return merged;
  }, [effectiveRawPrices, contractDetailsMap, activePositions]);

  // 6. Extract currencies from both positions AND effectiveBatchPrices
  // effectiveBatchPrices includes currencies for synthetic positions (target contracts user doesn't hold)
  const currencies = useMemo(() => {
    const positionCurrencies = activePositions.map(p => p.currency).filter((c): c is string => Boolean(c));
    const priceCurrencies = Object.values(effectiveBatchPrices).map(p => p.currency).filter((c): c is string => Boolean(c));
    return Array.from(new Set([...positionCurrencies, ...priceCurrencies]));
  }, [activePositions, effectiveBatchPrices]);

  const {
    fxRates,
    isLoading: fxRatesLoading,
    error: fxRatesError,
  } = useFxRates(currencies);

  // 7. Transform to unified PortfolioRow[]
  // Rows are always computed - if FX rates failed, USD values will be null and show "err"
  // Uses effectiveBatchPrices for progressive loading support
  const rows = useMemo((): PortfolioRow[] => {
    // Only return early if no positions AND not in diff mode.
    // In diff mode, we must still compute synthetic rows for target contracts
    // even when the user holds zero positions (new or fully-liquidated accounts).
    if (activePositions.length === 0 && !isDiffMode) return [];

    if (isDiffMode && targetPortfolio) {
      // Diff mode: use tradeCalculationService
      const diffPositions = tradeCalculationService.calculateDiffsForPositions(
        activePositions,
        accountSummary ?? null,
        targetPortfolio,
        tickerDataMap,
        excludedConIds,
        fxRates,
        effectiveBatchPrices,
        fullRefresh,
        contractDetailsMap
      );

      return diffPositions.map(pos => diffPositionToRow(pos, effectiveBatchPrices, timeZoneMap, fxRates));
    } else {
      // Non-diff mode: simple position to row conversion
      // Pass netLiq to calculate currentWeightPct
      const netLiq = accountSummary?.netLiquidationSecurity ?? 0;
      return activePositions.map(pos => positionToRow(
        pos,
        effectiveBatchPrices[pos.conId],
        netLiq,
        timeZoneMap,
        fxRates,
        contractDetailsMap[pos.conId]?.minSize,
        isOptionPosition(pos.secType)
          ? { quote: optionPricesMap[pos.conId], multiplier: optionMultiplierMap[pos.conId] }
          : undefined,
        "USD"
      ));
    }
  }, [activePositions, accountSummary, targetPortfolio, tickerDataMap, excludedConIds, effectiveBatchPrices, isDiffMode, timeZoneMap, fullRefresh, fxRates, contractDetailsMap, optionPricesMap, optionMultiplierMap]);

  // 8. Aggregate loading state
  // Note: fxRatesLoading is NOT included here - we render rows immediately with null USD values
  // and show "Updating rates..." indicator via isPartialData instead
  const isLoading =
    positionsLoading ||
    summaryLoading ||
    (conIds.length > 0 && pricesLoading) ||
    (conIds.length > 0 && contractDetailsLoading) ||
    (optionConIds.length > 0 && optionPricesLoading) ||
    (optionConIds.length > 0 && optionMultipliersLoading) ||
    (config.dataRequirements.tradableTimes && conIds.length > 0 && tradableTimesLoading);

  // 8b. Determine if we have partial data (rows exist but some data may be incomplete)
  // This happens when:
  // - Positions loaded but FX rates are still loading
  // - Batch prices are still loading progressively (some chunks completed, others pending)
  const hasNonUsdCurrencies = currencies.some(c => c.toUpperCase() !== "USD");
  const hasPricesLoadingProgressively = pricesLoading && pricesProgress.completed > 0 && pricesProgress.completed < pricesProgress.total;
  const isPartialData = rows.length > 0 && (
    (hasNonUsdCurrencies && fxRatesLoading) ||
    hasPricesLoadingProgressively
  );

  // 9. First error encountered
  const error = positionsError || summaryError || pricesError || tradableTimesError || contractDetailsError || fxRatesError || null;

  // 10. Most recent update timestamp
  const lastUpdatedAt = Math.max(
    positionsUpdatedAt || 0,
    summaryUpdatedAt || 0,
    pricesUpdatedAt || 0,
    optionPricesUpdatedAt || 0,
    tradableTimesUpdatedAt || 0
  );

  // 11. Refresh function -- awaitable via refetchQueries (resolves when data arrives)
  const normalizedNonUsdCurrencies = useMemo(() => {
    return Array.from(new Set(currencies.map(c => c.toUpperCase()))).filter(c => c !== "USD");
  }, [currencies]);

  const refresh = useCallback(async (): Promise<void> => {
    const opts = { throwOnError: true };
    const queries: Promise<void>[] = [
      queryClient.refetchQueries(
        { queryKey: accountQueryKeys.positions(userId), type: "active" },
        opts
      ),
      queryClient.refetchQueries(
        { queryKey: accountQueryKeys.summary(userId), type: "active" },
        opts
      ),
    ];
    if (conIds.length > 0) {
      queries.push(
        queryClient.refetchQueries({ queryKey: portfolioQueryKeys.batchPrices(conIds) }, opts),
        queryClient.refetchQueries({ queryKey: portfolioQueryKeys.contractDetails(conIds) }, opts),
      );
      if (config.dataRequirements.tradableTimes) {
        queries.push(
          queryClient.refetchQueries({ queryKey: portfolioQueryKeys.tradableTimes(conIds) }, opts),
        );
      }
    }
    if (optionConIds.length > 0) {
      queries.push(
        queryClient.refetchQueries({ queryKey: portfolioQueryKeys.optionPrices(optionConIds) }, opts),
        queryClient.refetchQueries({ queryKey: portfolioQueryKeys.optionMultipliers(optionConIds) }, opts),
      );
    }
    if (normalizedNonUsdCurrencies.length > 0) {
      queries.push(
        queryClient.refetchQueries({ queryKey: fxRatesQueryKeys.rates(normalizedNonUsdCurrencies), exact: true }, opts),
      );
    }
    await Promise.all(queries);
  }, [queryClient, userId, conIds, optionConIds, config.dataRequirements.tradableTimes, normalizedNonUsdCurrencies]);

  return {
    rows,
    accountSummary: accountSummary ?? null,
    isLoading,
    isPartialData,
    error,
    lastUpdatedAt,
    refresh,
  };
}

export default usePortfolioData;
